import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { RetireBanner } from "@/components/RetireNotice";
import { toast } from "sonner";
import { ArrowLeft, Pencil, MapPin, Mail, Phone, Save, X, GitMerge, Lock, MessageSquare, StickyNote, Trash2, Plus, Camera, Cake, UserPlus, Star, ChevronLeft, ChevronRight, ArrowLeftRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { PROFILE_FIELDS } from "@/lib/fields";

// Build a vCard and hand it to iOS/Android as an "Add to Contacts" download.
// Company is set to "VIP Guest (DSA)" so guests group together and are searchable.
const addToContacts = (profile) => {
  const raw = String(profile.name || "").trim();
  let family = raw, given = "";
  if (raw.includes(",")) { const [f, g] = raw.split(",").map((s) => s.trim()); family = f; given = g || ""; }
  const fn = given ? `${given} ${family}` : raw;
  const esc = (v) => String(v).replace(/([,;\\])/g, "\\$1");
  const lines = ["BEGIN:VCARD", "VERSION:3.0", `N:${esc(family)};${esc(given)};;;`, `FN:${esc(fn)}`, "ORG:VIP Guest (DSA)", "CATEGORIES:VIP Guests"];
  if (profile.phone) lines.push(`TEL;TYPE=CELL:${profile.phone}`);
  if (profile.mdx_email) lines.push(`EMAIL;TYPE=INTERNET:${profile.mdx_email}`);
  const city = [profile.city, profile.state, profile.country].filter(Boolean);
  if (city.length) lines.push(`ADR;TYPE=HOME:;;${esc(profile.city || "")};${esc(profile.state || "")};;;${esc(profile.country || "")}`);
  lines.push("END:VCARD");
  const blob = new Blob([lines.join("\r\n")], { type: "text/vcard;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${fn || "guest"}.vcf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 4000);
};
import { GlobalSearch } from "@/components/GlobalSearch";
import { ImageZoomViewer } from "@/components/ImageZoomViewer";
import { CelebrationsEditor, CelebrationsList } from "@/components/CelebrationsEditor";
import { AppBottomNav } from "@/components/BottomNav";
import { fileToResizedDataUrl } from "@/lib/image";
import { usePrivacy } from "@/contexts/PrivacyContext";
import { api } from "@/lib/api";
import { fuzzyMatch } from "@/lib/fuzzy";

const smsHref = (v) => `sms:${String(v || "").replace(/[^\d+]/g, "")}`;

const StatBox = ({ label, value, accent, testid }) => (
  <div className="rounded-2xl lg-glass p-4" data-testid={testid}>
    <div className="text-xs uppercase tracking-wider text-zinc-400">{label}</div>
    <div className={`mt-2 font-display text-2xl font-800 tracking-tight ${accent || "text-zinc-50"}`}>{value}</div>
  </div>
);

export default function GuestProfile() {
  const { name } = useParams();
  const decoded = decodeURIComponent(name);
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = () => { (location.key && location.key !== "default") ? navigate(-1) : navigate("/"); };
  const { financialPrivacy } = usePrivacy();
  const [profile, setProfile] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [form, setForm] = useState({});
  const [newName, setNewName] = useState("");
  const [renameTab, setRenameTab] = useState("rename");
  const [allProfiles, setAllProfiles] = useState([]);
  const [mergeQuery, setMergeQuery] = useState("");
  const [mergeTarget, setMergeTarget] = useState(null);
  const [merging, setMerging] = useState(false);
  const [dupSuggestions, setDupSuggestions] = useState([]);
  const [keepWhich, setKeepWhich] = useState("target"); // "target" = keep selected, "current" = keep this profile
  const [viewerSrc, setViewerSrc] = useState(null);
  const [celebOpen, setCelebOpen] = useState(false);
  const [celebDraft, setCelebDraft] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [reorderMode, setReorderMode] = useState(false);
  const photoInputRef = useRef();

  const load = useCallback(async () => {
    try {
      setProfile(await api.getProfile(decoded));
    } catch {
      toast.error("Guest profile not found");
      navigate("/");
    }
  }, [decoded, navigate]);

  useEffect(() => { load(); }, [load]);

  // Surface likely-duplicate guests (fuzzy name match) so they can be merged in a tap.
  useEffect(() => {
    let alive = true;
    api.duplicateProfiles(decoded)
      .then((groups) => {
        if (!alive) return;
        const grp = (groups || []).find((g) => (g.members || []).some((m) => m.name === decoded));
        setDupSuggestions(grp ? grp.members.filter((m) => m.name !== decoded) : []);
      })
      .catch(() => { if (alive) setDupSuggestions([]); });
    return () => { alive = false; };
  }, [decoded]);

  const openMergeWith = (targetName) => {
    setRenameTab("merge");
    setMergeQuery("");
    setKeepWhich("target");
    api.listProfiles()
      .then((ps) => {
        setAllProfiles(ps || []);
        const t = (ps || []).find((p) => p.name === targetName);
        setMergeTarget(t || { name: targetName, lifetime_tours: 0 });
      })
      .catch(() => { setAllProfiles([]); setMergeTarget({ name: targetName, lifetime_tours: 0 }); });
    setRenameOpen(true);
  };

  const openEdit = () => {
    setForm({ ...Object.fromEntries(PROFILE_FIELDS.map((f) => [f.key, profile[f.key] ?? ""])), celebrations: profile.celebrations || [], notes: profile.notes || "" });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    try {
      const updated = await api.updateProfile(decoded, form);
      setProfile(updated);
      setEditOpen(false);
      toast.success("Profile updated");
    } catch {
      toast.error("Update failed");
    }
  };

  const doRename = async () => {
    const target = newName.trim();
    if (!target) return;
    try {
      await api.renameProfile(decoded, target);
      setRenameOpen(false);
      toast.success(target === decoded ? "Saved" : `Renamed / merged into "${target}"`);
      navigate(`/profile/${encodeURIComponent(target)}`);
    } catch {
      toast.error("Rename failed");
    }
  };

  const openRename = () => {
    setNewName(decoded);
    setRenameTab("rename");
    setMergeQuery("");
    setMergeTarget(null);
    setKeepWhich("target");
    api.listProfiles().then((ps) => setAllProfiles(ps || [])).catch(() => setAllProfiles([]));
    setRenameOpen(true);
  };

  const doMerge = async () => {
    if (!mergeTarget) return;
    // keepWhich decides which spelling survives: keep the selected guest (rename this profile
    // into it) or keep this profile (rename the selected guest into it).
    const keepName = keepWhich === "current" ? decoded : mergeTarget.name;
    const removeName = keepWhich === "current" ? mergeTarget.name : decoded;
    setMerging(true);
    try {
      await api.renameProfile(removeName, keepName);
      setRenameOpen(false);
      toast.success(`Merged into "${keepName}"`);
      navigate(`/profile/${encodeURIComponent(keepName)}`);
    } catch {
      toast.error("Merge failed");
    } finally {
      setMerging(false);
    }
  };

  const mergeMatches = allProfiles
    .filter((p) => p.name && p.name !== decoded)
    .filter((p) => fuzzyMatch(p.name, mergeQuery))
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
    .slice(0, 8);

  const deletePhoto = async (p) => {
    if (!window.confirm("Remove this photo?")) return;
    try {
      const updated = await api.deleteProfilePhoto(decoded, { source: p.source, tour_id: p.tour_id, index: p.index });
      setProfile(updated);
      toast.success("Photo removed");
    } catch {
      toast.error("Couldn't remove photo");
    }
  };

  const setAvatar = async (p) => {
    try {
      const updated = await api.updateProfile(decoded, { avatar_id: p.id });
      setProfile(updated);
      toast.success("Profile photo set");
    } catch {
      toast.error("Couldn't set profile photo");
    }
  };

  const movePhoto = async (id, dir) => {
    const ids = photos.map((p) => p.id);
    const i = ids.indexOf(id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    setProfile((prev) => ({ ...prev, gallery: [...prev.gallery].sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id)) }));
    try {
      await api.updateProfile(decoded, { photo_order: ids });
    } catch {
      toast.error("Couldn't reorder");
      load();
    }
  };

  const openPhoto = async (p) => {
    setViewerSrc(p.thumb);
    try {
      const { full } = await api.profilePhoto(decoded, { source: p.source, tour_id: p.tour_id, index: p.index });
      if (full) setViewerSrc(full);
    } catch { /* keep thumb */ }
  };

  const addPhoto = async (file) => {
    if (!file) return;
    if (!/(png|jpe?g|webp|heic|heif)/i.test(file.type)) { toast.error("Upload a photo (JPG, PNG or WEBP)"); return; }
    setUploading(true);
    try {
      const dataUrl = await fileToResizedDataUrl(file, 1400, 0.8);
      const next = [...(profile.photos || []), dataUrl];
      const updated = await api.updateProfile(decoded, { photos: next });
      setProfile(updated);
      toast.success("Photo added");
    } catch {
      toast.error("Couldn't add that photo");
    } finally {
      setUploading(false);
    }
  };

  const openCelebrations = () => { setCelebDraft(profile.celebrations || []); setCelebOpen(true); };
  const saveCelebrations = async () => {
    try {
      const updated = await api.updateProfile(decoded, { celebrations: celebDraft });
      setProfile(updated);
      setCelebOpen(false);
      toast.success("Celebrations saved");
    } catch {
      toast.error("Couldn't save celebrations");
    }
  };

  if (!profile) return <div className="p-10 text-zinc-400">Loading…</div>;

  const loc = [profile.city, profile.state, profile.country].filter(Boolean).join(", ");
  const photos = (profile.gallery || []).map((g) => ({ ...g, key: `${g.source}-${g.tour_id || ""}-${g.index}` }));
  const avatarSrc = profile.avatar || photos[0]?.thumb || null;


  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 border-b border-zinc-800/80 bg-zinc-950/80">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url(/logo.jpg)" }} />
        <div className="absolute inset-0 plaid-veil" />
        <div className="relative mx-auto flex max-w-4xl items-center justify-between px-5 py-2.5">
          <button onClick={goBack} className="flex items-center gap-2 text-sm text-zinc-100 plaid-text hover:text-white" data-testid="profile-back-btn">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <div className="flex items-center gap-2">
            <GlobalSearch variant="icon" />
            <Button onClick={openRename} variant="outline" className="hdr-chip border" data-testid="rename-guest-btn">
              <GitMerge className="mr-1.5 h-4 w-4" /> Rename / Merge
            </Button>
            <Button onClick={openEdit} className="bg-[#9B2226] text-white hover:bg-[#7A1A1E]" data-testid="edit-profile-btn">
              <Pencil className="mr-1.5 h-4 w-4" /> Edit
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 py-8 pb-28">
        <div className="mb-6">
          <RetireBanner onShowExport={() => { sessionStorage.setItem("activeTab", "settings"); sessionStorage.setItem("sd_focus_export", "1"); navigate("/"); }} />
        </div>
        <div className="flex items-center gap-4">
          {avatarSrc && (
            <img
              src={avatarSrc}
              alt={profile.name}
              onClick={() => setViewerSrc(avatarSrc)}
              className="h-16 w-16 shrink-0 cursor-zoom-in rounded-full border border-zinc-800 object-cover transition-opacity hover:opacity-90 sm:h-20 sm:w-20"
              data-testid="profile-avatar"
            />
          )}
          <h1 className="font-display text-3xl font-800 tracking-tight text-zinc-50 sm:text-4xl" data-testid="profile-title">{profile.name}</h1>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-zinc-300">
          {loc && <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4 text-zinc-400" /> {loc}</span>}
          {profile.mdx_email && <a href={`mailto:${profile.mdx_email}`} className="inline-flex items-center gap-1.5 hover:text-[#E0575B]"><Mail className="h-4 w-4 text-zinc-400" /> {profile.mdx_email}</a>}
          {profile.phone && (
            <span className="inline-flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5"><Phone className="h-4 w-4 text-zinc-400" /> {profile.phone}</span>
              <a href={smsHref(profile.phone)} data-testid="profile-text-btn"
                 className="inline-flex items-center gap-1 rounded-full border border-[#9B2226]/50 bg-[#9B2226]/10 px-3 py-1 text-xs font-600 text-[#E0575B] transition-colors hover:bg-[#9B2226]/20">
                <MessageSquare className="h-3.5 w-3.5" /> Text
              </a>
            </span>
          )}
          <button onClick={() => addToContacts(profile)} data-testid="add-to-contacts-btn"
            className="inline-flex items-center gap-1.5 rounded-full border border-zinc-700 bg-zinc-900 px-3 py-1 text-xs font-600 text-zinc-200 transition-colors hover:border-[#9B2226] hover:text-[#E0575B]">
            <UserPlus className="h-3.5 w-3.5" /> Add to Contacts
          </button>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatBox label="Lifetime Tours" value={profile.lifetime_tours} accent="text-[#E0575B]" testid="stat-lifetime-tours" />
          <StatBox label="Lifetime Days" value={profile.lifetime_days} accent="text-zinc-100" testid="stat-lifetime-days" />
          {!financialPrivacy && (
            <>
              <StatBox label="Lifetime Gratuity" value={`$${(profile.lifetime_gratuity || 0).toLocaleString()}`} accent="text-emerald-300" testid="stat-lifetime-gratuity" />
              <StatBox label="Avg Daily Gratuity" value={`$${(profile.average_daily_gratuity || 0).toLocaleString()}`} accent="text-zinc-100" testid="stat-avg-daily-gratuity" />
            </>
          )}
        </div>

        {dupSuggestions.length > 0 && (
          <div className="mt-5 rounded-2xl border border-amber-500/30 bg-amber-500/[0.07] p-4" data-testid="merge-suggestions">
            <div className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-300"><GitMerge className="h-5 w-5" /></span>
              <div className="min-w-0 flex-1">
                <h3 className="font-display text-sm font-700 tracking-tight text-amber-200">Looks like a duplicate?</h3>
                <p className="mt-0.5 text-xs text-amber-200/70">{dupSuggestions.length === 1 ? "This guest may be the same person as:" : "This guest may be the same as these:"}</p>
                <div className="mt-2 space-y-1.5">
                  {dupSuggestions.map((m) => (
                    <div key={m.name} className="flex items-center justify-between gap-2 rounded-xl border border-zinc-800 bg-zinc-900/60 px-3 py-2" data-testid={`merge-suggestion-${m.name}`}>
                      <button onClick={() => navigate(`/profile/${encodeURIComponent(m.name)}`)} className="min-w-0 flex-1 truncate text-left text-sm font-600 text-zinc-100 hover:text-[#E0575B]">
                        {m.name} <span className="font-400 text-zinc-500">· {m.tours} tour{m.tours === 1 ? "" : "s"}</span>
                      </button>
                      <Button onClick={() => openMergeWith(m.name)} size="sm" className="h-7 shrink-0 bg-[#9B2226] px-2.5 text-xs text-white hover:bg-[#7A1A1E]" data-testid={`merge-suggestion-btn-${m.name}`}>
                        <GitMerge className="mr-1 h-3 w-3" /> Merge
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {(profile.celebrations || []).length > 0 && (
          <div className="mt-5" data-testid="profile-celebrations">
            <h2 className="mb-2 font-display text-sm font-700 tracking-tight text-zinc-200">Celebrations</h2>
            <CelebrationsList value={profile.celebrations} />
          </div>
        )}

        {profile.notes && (
          <div className="mt-5" data-testid="profile-notes">
            <h2 className="mb-2 flex items-center gap-1.5 font-display text-sm font-700 tracking-tight text-zinc-200">
              <StickyNote className="h-4 w-4 text-zinc-400" /> Notes
            </h2>
            <p className="whitespace-pre-wrap rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 text-sm text-zinc-300" data-testid="profile-notes-text">{profile.notes}</p>
          </div>
        )}

        <h2 className="mb-3 mt-8 font-display text-base font-700 tracking-tight text-zinc-100">Tour List</h2>
        <div className="overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900/40" data-testid="profile-tour-list">
          {profile.tour_list.length === 0 ? (
            <div className="p-6 text-center text-sm text-zinc-400">No tours yet.</div>
          ) : (
            profile.tour_list.map((t) => (
              <div
                key={t.id}
                onClick={() => navigate(`/guest/${t.id}`)}
                data-testid={`profile-tour-${t.id}`}
                className="flex cursor-pointer items-center justify-between border-b border-zinc-800/70 px-4 py-3 transition-colors duration-150 last:border-b-0 hover:bg-white/5"
              >
                <div className="flex items-center gap-3">
                  <span className="font-500 text-zinc-100">{t.title}</span>
                  <span className="text-xs text-zinc-400" data-testid={`profile-tour-days-${t.id}`}>{t.days} {t.days === 1 ? "day" : "days"}</span>
                </div>
                <span className={`text-sm ${t.gratuity != null ? "text-emerald-300" : "text-zinc-100"}`}>
                  {financialPrivacy ? <Lock className="h-3.5 w-3.5 text-zinc-600" /> : (t.gratuity != null ? `$${Number(t.gratuity).toLocaleString()}` : "—")}
                </span>
              </div>
            ))
          )}
        </div>

        {/* Photos — party photos across tours + photos added here directly */}
        <section className="mt-8" data-testid="profile-photos-section">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center gap-1.5 font-display text-base font-700 tracking-tight text-zinc-100">
              <Camera className="h-4 w-4 text-[#E0575B]" /> Photos
            </h2>
            <div className="flex items-center gap-2">
              {photos.length > 0 && <span className="text-xs text-zinc-400">{photos.length} photo{photos.length === 1 ? "" : "s"}</span>}
              {photos.length > 1 && (
                <Button onClick={() => setReorderMode((v) => !v)} variant="outline" size="sm" className={`hdr-chip border ${reorderMode ? "!border-[#E0575B] !text-[#E0575B]" : ""}`} data-testid="reorder-photos-btn">
                  <ArrowLeftRight className="mr-1.5 h-3.5 w-3.5" /> {reorderMode ? "Done" : "Reorder"}
                </Button>
              )}
              <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { addPhoto(e.target.files?.[0]); if (photoInputRef.current) photoInputRef.current.value = ""; }} data-testid="profile-photo-input" />
              <Button onClick={() => photoInputRef.current?.click()} disabled={uploading} variant="outline" size="sm" className="hdr-chip border" data-testid="add-photo-btn">
                <Plus className="mr-1.5 h-3.5 w-3.5" /> {uploading ? "Adding…" : "Add photo"}
              </Button>
            </div>
          </div>
          {photos.length === 0 ? (
            <div className="rounded-lg border border-dashed border-zinc-800 bg-zinc-900/30 p-6 text-center text-sm text-zinc-400" data-testid="profile-photos-empty">
              No photos yet. Tap <b className="text-zinc-200">Add photo</b>, or add a photo on any tour — they all show up here.
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
              {photos.map((p, idx) => {
                const isAvatar = profile.avatar_id ? profile.avatar_id === p.id : (idx === 0 && !!avatarSrc);
                return (
                <div key={p.key} className="group relative aspect-square" data-testid={`profile-photo-${p.key}`}>
                  <img
                    src={p.thumb}
                    alt={p.title || profile.name}
                    loading="lazy"
                    onClick={() => !reorderMode && openPhoto(p)}
                    className={`h-full w-full rounded-lg border object-cover transition-opacity hover:opacity-90 ${reorderMode ? "cursor-default" : "cursor-zoom-in"} ${isAvatar ? "border-[#E0575B] ring-2 ring-[#E0575B]/50" : "border-zinc-800"}`}
                    data-testid={`profile-photo-img-${p.key}`}
                  />
                  {isAvatar && !reorderMode && (
                    <span className="pointer-events-none absolute left-1.5 top-1.5 flex items-center gap-1 rounded-full bg-[#9B2226] px-1.5 py-0.5 text-[9px] font-700 uppercase text-white" data-testid={`profile-photo-current-${p.key}`}>
                      <Star className="h-2.5 w-2.5 fill-white" /> Profile
                    </span>
                  )}
                  {reorderMode ? (
                    <div className="absolute inset-x-1 bottom-1.5 flex items-center justify-between gap-1">
                      <button onClick={(e) => { e.stopPropagation(); movePhoto(p.id, -1); }} disabled={idx === 0} className="flex h-7 w-7 items-center justify-center rounded-full bg-black/70 text-white backdrop-blur-sm disabled:opacity-30" data-testid={`profile-photo-left-${p.key}`} aria-label="Move left">
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); movePhoto(p.id, 1); }} disabled={idx === photos.length - 1} className="flex h-7 w-7 items-center justify-center rounded-full bg-black/70 text-white backdrop-blur-sm disabled:opacity-30" data-testid={`profile-photo-right-${p.key}`} aria-label="Move right">
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      {!isAvatar && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setAvatar(p); }}
                          className="absolute bottom-1.5 left-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-white opacity-0 backdrop-blur-sm transition-opacity hover:bg-[#9B2226] group-hover:opacity-100"
                          data-testid={`profile-photo-setavatar-${p.key}`}
                          aria-label="Set as profile photo"
                          title="Set as profile photo"
                        >
                          <Star className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); deletePhoto(p); }}
                        className="absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-white opacity-0 backdrop-blur-sm transition-opacity hover:bg-red-600/80 group-hover:opacity-100"
                        data-testid={`profile-photo-delete-${p.key}`}
                        aria-label="Remove photo"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Celebrations — quick add / manage */}
        <section className="mt-8" data-testid="profile-celebrations-section">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center gap-1.5 font-display text-base font-700 tracking-tight text-zinc-100">
              <Cake className="h-4 w-4 text-[#E0575B]" /> Celebrations
            </h2>
            <Button onClick={openCelebrations} variant="outline" size="sm" className="hdr-chip border" data-testid="add-celebration-btn">
              <Plus className="mr-1.5 h-3.5 w-3.5" /> Add / edit
            </Button>
          </div>
          {(profile.celebrations || []).length > 0 ? (
            <CelebrationsList value={profile.celebrations} />
          ) : (
            <div className="rounded-lg border border-dashed border-zinc-800 bg-zinc-900/30 p-6 text-center text-sm text-zinc-400" data-testid="profile-celebrations-empty">
              No celebrations yet. Add a birthday or anniversary to get reminders.
            </div>
          )}
        </section>
      </main>

      {/* Edit profile */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto border-zinc-800 bg-zinc-950" data-testid="edit-profile-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Edit guest profile</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 py-2 md:grid-cols-2">
            {PROFILE_FIELDS.map((f) => (
              <div key={f.key}>
                <Label className="mb-1.5 block text-xs uppercase tracking-wide text-zinc-400">{f.label}</Label>
                <Input
                  value={form[f.key] ?? ""}
                  onChange={(e) => setForm((v) => ({ ...v, [f.key]: e.target.value }))}
                  className="border-zinc-800 bg-zinc-900 focus-visible:ring-[#E0575B]/70"
                  data-testid={`edit-profile-${f.key}`}
                />
              </div>
            ))}
          </div>
          <div className="border-t border-zinc-800 pt-3">
            <Label className="mb-2 block text-xs uppercase tracking-wide text-zinc-400">Celebrations (birthdays &amp; anniversaries)</Label>
            <CelebrationsEditor value={form.celebrations || []} onChange={(c) => setForm((v) => ({ ...v, celebrations: c }))} />
          </div>
          <div className="border-t border-zinc-800 pt-3">
            <Label className="mb-2 flex items-center gap-1.5 text-xs uppercase tracking-wide text-zinc-400"><StickyNote className="h-3.5 w-3.5 text-zinc-400" /> Notes</Label>
            <Textarea
              value={form.notes ?? ""}
              onChange={(e) => setForm((v) => ({ ...v, notes: e.target.value }))}
              placeholder="Private notes about this guest (preferences, allergies, anything to remember)…"
              rows={4}
              className="border-zinc-800 bg-zinc-900 text-sm focus-visible:ring-[#E0575B]/70"
              data-testid="edit-profile-notes"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)}><X className="mr-1.5 h-4 w-4" /> Cancel</Button>
            <Button onClick={saveEdit} className="bg-[#9B2226] text-white hover:bg-[#7A1A1E]" data-testid="save-profile-btn"><Save className="mr-1.5 h-4 w-4" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename / merge */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="max-w-md border-zinc-800 bg-zinc-950" data-testid="rename-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Rename or merge guest</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-1 rounded-xl bg-zinc-900 p-1" data-testid="rename-merge-tabs">
            <button
              onClick={() => setRenameTab("rename")}
              className={`rounded-lg px-3 py-1.5 text-sm font-600 transition-colors ${renameTab === "rename" ? "bg-[#9B2226] text-white" : "text-zinc-400 hover:text-zinc-200"}`}
              data-testid="tab-rename"
            >
              Rename
            </button>
            <button
              onClick={() => setRenameTab("merge")}
              className={`rounded-lg px-3 py-1.5 text-sm font-600 transition-colors ${renameTab === "merge" ? "bg-[#9B2226] text-white" : "text-zinc-400 hover:text-zinc-200"}`}
              data-testid="tab-merge"
            >
              Merge into another
            </button>
          </div>

          {renameTab === "rename" ? (
            <div className="space-y-3">
              <p className="text-sm text-zinc-400">Change <span className="font-600 text-zinc-200">{decoded}</span>'s name across all their tours.</p>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="border-zinc-800 bg-zinc-900 focus-visible:ring-[#E0575B]/70"
                placeholder="Last, First"
                data-testid="rename-input"
              />
              <DialogFooter>
                <Button variant="ghost" onClick={() => setRenameOpen(false)}><X className="mr-1.5 h-4 w-4" /> Cancel</Button>
                <Button onClick={doRename} className="bg-[#9B2226] text-white hover:bg-[#7A1A1E]" data-testid="confirm-rename-btn"><Save className="mr-1.5 h-4 w-4" /> Apply</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-zinc-400">Find the other spelling, then choose which one to keep. All tours, photos and stats merge into the kept guest.</p>
              <Input
                value={mergeQuery}
                onChange={(e) => { setMergeQuery(e.target.value); setMergeTarget(null); }}
                className="border-zinc-800 bg-zinc-900 focus-visible:ring-[#E0575B]/70"
                placeholder="Search guests by name…"
                data-testid="merge-search-input"
                autoFocus
              />
              <div className="max-h-56 space-y-1 overflow-y-auto" data-testid="merge-results">
                {mergeMatches.length === 0 ? (
                  <p className="px-1 py-2 text-sm text-zinc-500" data-testid="merge-no-results">
                    {allProfiles.length <= 1 ? "No other guests to merge with yet." : "No matching guests."}
                  </p>
                ) : (
                  mergeMatches.map((p) => {
                    const sel = mergeTarget?.name === p.name;
                    return (
                      <button
                        key={p.name}
                        onClick={() => { setMergeTarget(p); setKeepWhich("target"); }}
                        className={`flex w-full items-center justify-between gap-2 rounded-xl border px-3 py-2 text-left transition-colors ${sel ? "border-[#E0575B] bg-[#9B2226]/15" : "border-zinc-800 bg-zinc-900 hover:bg-zinc-800/70"}`}
                        data-testid={`merge-candidate-${p.name}`}
                      >
                        <span className="min-w-0 flex-1 truncate text-sm font-600 text-zinc-100">{p.name}</span>
                        <span className="shrink-0 text-xs text-zinc-500">{p.lifetime_tours || 0} tour{(p.lifetime_tours || 0) === 1 ? "" : "s"}</span>
                        {sel && <span className="shrink-0 rounded-full bg-[#E0575B] px-1.5 py-0.5 text-[10px] font-700 uppercase text-white">Keep</span>}
                      </button>
                    );
                  })
                )}
              </div>
              {mergeTarget && (() => {
                const keepName = keepWhich === "current" ? decoded : mergeTarget.name;
                const removeName = keepWhich === "current" ? mergeTarget.name : decoded;
                const opt = (active) => active ? "border-[#E0575B] bg-[#9B2226]/20 text-zinc-50" : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800/70";
                const cEmail = profile.mdx_email || "", cPhone = profile.phone || "";
                const tEmail = mergeTarget.mdx_email || "", tPhone = mergeTarget.phone || "";
                const emailConflict = cEmail && tEmail && cEmail !== tEmail;
                const phoneConflict = cPhone && tPhone && cPhone !== tPhone;
                const showHints = emailConflict || phoneConflict;
                const hintFor = (email, phone) => [emailConflict ? (email || "—") : null, phoneConflict ? (phone || "—") : null].filter(Boolean).join(" · ");
                return (
                  <div className="space-y-2" data-testid="merge-keep-choice">
                    <p className="text-xs font-600 uppercase tracking-wider text-zinc-500">Which spelling to keep?</p>
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={() => setKeepWhich("target")} className={`flex flex-col gap-0.5 rounded-xl border px-3 py-2 text-left transition-colors ${opt(keepWhich === "target")}`} data-testid="keep-target">
                        <span className="truncate text-sm font-600">{mergeTarget.name}</span>
                        {showHints && <span className="truncate text-[10px] font-400 text-zinc-400" data-testid="keep-target-contact">Keeps: {hintFor(tEmail, tPhone) || "no contact"}</span>}
                      </button>
                      <button onClick={() => setKeepWhich("current")} className={`flex flex-col gap-0.5 rounded-xl border px-3 py-2 text-left transition-colors ${opt(keepWhich === "current")}`} data-testid="keep-current">
                        <span className="truncate text-sm font-600">{decoded}</span>
                        {showHints && <span className="truncate text-[10px] font-400 text-zinc-400" data-testid="keep-current-contact">Keeps: {hintFor(cEmail, cPhone) || "no contact"}</span>}
                      </button>
                    </div>
                    <div className="rounded-xl border border-[#E0575B]/40 bg-[#9B2226]/10 p-3 text-xs leading-relaxed text-zinc-200" data-testid="merge-summary">
                      <GitMerge className="mr-1 inline h-3.5 w-3.5 text-[#E0575B]" />
                      Keeps <span className="font-700 text-zinc-50">{keepName}</span> · removes <span className="font-700 text-zinc-50">{removeName}</span>. All tours move into <span className="font-700 text-zinc-50">{keepName}</span>.{showHints ? " Contact info from the kept guest is used." : ""} This can't be undone.
                    </div>
                  </div>
                );
              })()}
              <DialogFooter>
                <Button variant="ghost" onClick={() => setRenameOpen(false)}><X className="mr-1.5 h-4 w-4" /> Cancel</Button>
                <Button onClick={doMerge} disabled={!mergeTarget || merging} className="bg-[#9B2226] text-white hover:bg-[#7A1A1E] disabled:opacity-50" data-testid="confirm-merge-btn">
                  <GitMerge className="mr-1.5 h-4 w-4" /> {merging ? "Merging…" : mergeTarget ? `Merge into ${keepWhich === "current" ? decoded : mergeTarget.name}` : "Merge"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ImageZoomViewer
        open={!!viewerSrc}
        src={viewerSrc}
        alt={profile.name}
        onClose={() => setViewerSrc(null)}
        testid="profile-photo-viewer"
      />

      {/* Add / edit celebrations */}
      <Dialog open={celebOpen} onOpenChange={setCelebOpen}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto border-zinc-800 bg-zinc-950" data-testid="celebrations-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display tracking-tight"><Cake className="h-5 w-5 text-[#E0575B]" /> Celebrations</DialogTitle>
            <p className="text-sm text-zinc-400">Add birthdays &amp; anniversaries so you get a heads-up before the tour.</p>
          </DialogHeader>
          <CelebrationsEditor value={celebDraft} onChange={setCelebDraft} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCelebOpen(false)}><X className="mr-1.5 h-4 w-4" /> Cancel</Button>
            <Button onClick={saveCelebrations} className="bg-[#9B2226] text-white hover:bg-[#7A1A1E]" data-testid="save-celebrations-btn"><Save className="mr-1.5 h-4 w-4" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AppBottomNav />
    </div>
  );
}
